import type { EventListItem } from '../types'

export function formatDate(value: string | null | undefined) {
  if (!value) return 'Sem data'
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day))
}

// A season as a card shows it, short enough for one line: "14 out. – 3 nov.
// 2026", the year once when both days share it; one day, "14 out. 2026".
export function formatDateRange(first: string | null | undefined, last?: string | null) {
  if (!first) return 'Sem data'
  const parts = (value: string) => {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number)
    const name = new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(year, month - 1, 1))
    return { year, label: `${day} ${name}` }
  }
  const start = parts(first)
  if (!last || last.slice(0, 10) === first.slice(0, 10)) return `${start.label} ${start.year}`
  const end = parts(last)
  return start.year === end.year
    ? `${start.label} – ${end.label} ${end.year}`
    : `${start.label} ${start.year} – ${end.label} ${end.year}`
}

// "Hoje, 12:04", "Ontem, 18:02" or "12/10, 09:01", in Brasília time.
export function formatWhen(value: string) {
  const date = new Date(value)
  const day = (offset: number) => new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short' })
    .format(new Date(Date.now() - offset * 86_400_000))
  const time = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }).format(date)
  const label = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short' }).format(date)
  if (label === day(0)) return `Hoje, ${time}`
  if (label === day(1)) return `Ontem, ${time}`
  return `${label.slice(0, 5)}, ${time}`
}

// "qua, 21/10", in Brasília time: a day with its weekday, to plan by.
export function formatWeekday(value: string) {
  const date = new Date(value)
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: 'America/Sao_Paulo' }).format(date).replace('.', '')
  const day = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' }).format(date)
  return `${weekday}, ${day}`
}

// Where an event happens, as the app's cards write it: the place's name when
// it is one, else how many.
export function formatPlaces(names: string[]) {
  if (names.length === 1) return names[0]
  return names.length ? `${names.length} locais` : 'Sem local'
}

// An event as a card shows it (PRD.md, item 75): with dates ahead, by what
// comes, its places and season from today on; ended, by all it had.
export const eventPlaces = (event: EventListItem) =>
  formatPlaces(event.next_date && event.upcoming_place_names ? event.upcoming_place_names : event.place_names)
export const eventDates = (event: EventListItem) =>
  formatDateRange(event.next_date ? event.upcoming_first_date : event.first_date, event.last_date)
// A season's days short enough to leave room for its place, as Google Calendar
// writes them: "2 out.", "1–15 nov.", "20 nov. – 5 dez."; the year only when it
// is not this one.
export function formatSeasonDays(first: string, last: string | null | undefined, now = new Date()) {
  const parts = (value: string) => {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number)
    return { year, month, day, name: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(year, month - 1, 1)) }
  }
  const start = parts(first)
  const end = parts(last || first)
  const thisYear = new Intl.DateTimeFormat('en', { year: 'numeric', timeZone: 'America/Sao_Paulo' }).format(now)
  const year = (value: number) => (String(value) === thisYear ? '' : ` ${value}`)
  if (start.year !== end.year) return `${start.day} ${start.name} ${start.year} – ${end.day} ${end.name} ${end.year}`
  if (start.month !== end.month) return `${start.day} ${start.name} – ${end.day} ${end.name}${year(end.year)}`
  return `${start.day === end.day ? start.day : `${start.day}–${end.day}`} ${end.name}${year(end.year)}`
}

// With two or more seasons in what the card shows, each with its own days and
// place (PRD.md, item 118): a day at one theater and the next at another read
// as one season from the first to the second. With dates ahead, only the
// seasons ahead; one season keeps the lines above.
export function eventSeasons(event: EventListItem, now = new Date()) {
  const seasons = (event.seasons ?? []).filter((season) => season.starts_on)
  const shown = event.next_date ? seasons.filter((season) => season.upcoming) : seasons
  return shown.length > 1
    ? shown.map((season) => ({ place: season.place || 'Sem local', dates: formatSeasonDays(season.starts_on as string, season.ends_on, now) }))
    : []
}

// Where the app's card crops the photo: its framing, or the centre (PRD.md,
// items 40 and 104).
export const cardImagePosition = (event: { image_focus_x?: number | null; image_focus_y?: number | null }) =>
  `${(event.image_focus_x ?? 0.5) * 100}% ${(event.image_focus_y ?? 0.5) * 100}%`

export function formatDateTime(value: string | null | undefined) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(value))
}

export function statusLabel(status: string) {
  if (status === 'published') return 'Publicado'
  if (status === 'canceled') return 'Cancelado'
  if (status === 'deleted') return 'Excluído'
  return status
}
